/** @format */

import {TFunction} from 'i18next';

import {add, isBefore} from 'date-fns';

import {MultilineChartInterface, PointName} from 'components/charts/multiline-chart/model/common';

import {formatDateToString} from 'helpers/dateHelpers';

import {generateGraphPointName} from 'helpers/miscHelpers';

import {Discreteness, Period} from 'components/PeriodSelector/model';

import {CommonChartData} from 'components/charts/multiline-chart/model/charts';

import {
  ALL_CHART_IDS,
  ALL_PROPERTY_IDS,
  ALL_SCATTER_IDS,
  ALL_SENSOR_CHART_IDS,
} from '../constants/allChartIds';
import {DTO_DATE_FORMAT} from '../constants/common';
import getAllMediaplanUniqueChartIds from '../helpers/getAllMediaplanUniqueChartIds';
import isPlanUnreachable from '../helpers/isPlanUnreachable';
import {AnalyticGoalFromQuery, FunnelGoalStatFromQuery, MediaplanFromQuery} from '../models/api';
import {ChartIds} from '../models/chartIds';
import {DateList, GraphComplexValuesList, GraphStat} from '../models/common';
import {WidgetConditionsList, WidgetSettingsList} from '../models/spaces';
import {WidgetBlock} from '../models/widgets';
import {statsSensors} from './calculate-stats/statsSensors';
import getAllChartIdsPoint from './getAllChartIdsPoint';
import getWidgetSettings from './getWidgetSettings';
import mapChartDataPoint from './mapChartDataPoint';
import MemoryHandler from './memory-handler/memoryHandler';
import getStatsTypeGoal from './widget-stats/getStatsTypeGoal';
import {SensorsList} from '../helpers/sensors/types';
import {ScatterData} from '../models/scatters';
import {addForecast} from './forecast/addForecast';
import {sortCharts} from './sortCharts';
import {addTooltips} from './tooltip/addTooltips';

export interface MapChartsDataOptions {
  mediaplansDto: ReadonlyArray<MediaplanFromQuery>;
  graphStats: GraphStat[];
  graphComplexValues: GraphComplexValuesList;
  sensors?: SensorsList;
  analyticGoals: ReadonlyArray<AnalyticGoalFromQuery>;
  funnelGoalStats?: ReadonlyArray<FunnelGoalStatFromQuery>;
  startDate: string;
  endDate: string;
  period: Period;
  rechartsDates: string[];
  now: Date;
  companyId: string;
  t: TFunction;
  logs?: ScatterData;
  notes?: ScatterData;
  refetchNotes?: () => void;
  activeDiscreteness?: Discreteness;
}

export interface MapChartsDataResult {
  charts: MultilineChartInterface<ChartIds>;
  blocks: WidgetBlock[];
  widgetSettings: WidgetSettingsList;
  widgetConditions: WidgetConditionsList;
  todayPointName: string;
}

/**
 * Этот метод - точка входа в mapping
 * Все ресурсы, которые есть в папке mapping, используются исключительно в этом методе
 * @param mediaplansDto
 * @param graphComplexValues
 * @param graphStats
 * @param sensors
 * @param startDate
 * @param endDate
 * @param period
 * @param now
 * @param companyId
 * @param analyticGoals
 * @param funnelGoalStats
 * @param t
 * @param logs
 * @param notes
 * @param refetchNotes
 * @param rechartsDates
 * @param activeDiscreteness
 */

const mapChartsData = ({
  mediaplansDto,
  graphComplexValues,
  graphStats,
  sensors,
  startDate,
  endDate,
  period,
  now,
  companyId,
  analyticGoals,
  funnelGoalStats,
  t,
  logs,
  notes,
  refetchNotes,
  rechartsDates,
  activeDiscreteness,
}: MapChartsDataOptions): MapChartsDataResult => {
  // Создаем объект, внутри которого будут храниться промежуточные данные и финальный результат

  const {
    resultCharts,
    resultWidgetBlocks,
    getPreviousValue,
    addChartData,
    addXAxis,
    addPlan,
    setPreviousValue,
    pushWidgetCommonId,
    updateCharts,
    addAnalyticGoalChartBySeries,
    addScatterDataLogs,
    addScatterDataNotes,
  } = new MemoryHandler();

  if (analyticGoals.length > 0) {
    analyticGoals.forEach(goal => {
      pushWidgetCommonId('funnel', goal.name as ChartIds);
    });
  }

  // Сегодня и вчера.
  // Берем за истину, что СЕГОДНЯ ДАННЫХ ЕЩЕ НЕТ.

  const today = formatDateToString(now, DTO_DATE_FORMAT);
  const yesterdayDate = add(now, {days: -1});
  const yesterday = isBefore(yesterdayDate, startDate)
    ? undefined
    : formatDateToString(yesterdayDate, DTO_DATE_FORMAT);

  // Нужно сохранить PointName сегодняшнего дня - пригодится при составлении статистики в виджетах

  let todayPointName: PointName = '';

  // Мапим результаты для графиков

  const dateIndexByDate = new Map<string, number>();
  const pointNamesList: PointName[] = [];
  const dateList: DateList = {};
  rechartsDates.forEach((date, index) => {
    dateIndexByDate.set(date, index);
  });

  // Предрасчёт рядов для динамических целей воронки
  const analyticsGoalsMeta = (analyticGoals || []).map(g => ({
    goalId: g.id,
    chartId: g.name as ChartIds,
  }));
  const byGoal: Record<number, number[]> = {};
  if (analyticGoals && analyticGoals.length > 0 && funnelGoalStats) {
    analyticGoals.forEach(g => {
      byGoal[g.id] = new Array<number>(rechartsDates.length).fill(0);
    });
    funnelGoalStats.forEach(s => {
      const idx = dateIndexByDate.get(s.date);
      if (idx !== undefined && byGoal[s.goalId]) {
        byGoal[s.goalId][idx] += s.conversions || 0;
      }
    });
  }

  const funnelValuesList =
    funnelGoalStats?.reduce<Record<string, number>>((acc, cur) => {
      acc[cur.date] = (acc[cur.date] ?? 0) + (cur.conversions ?? 0);
      return acc;
    }, {}) || {};

  rechartsDates.forEach((date, index) => {
    // Мапим данные за этот день (пойнт)

    // Узнаем уникальный name для точки recharts

    const pointName: PointName = generateGraphPointName(index);

    if (date === today) {
      todayPointName = pointName;
    }

    pointNamesList[index] = pointName;
    dateList[pointName] = date;

    // Формируем значения для xAxis

    addXAxis(pointName, date, today);

    // Собираем все id графиков, которые пришли с сервера за этот день

    const graphStat = graphStats.find(d => d.date === date);
    const allChartIdsPoint = getAllChartIdsPoint(graphStat);

    // Делать ли прогноз на этот день

    const allGraphStatKeys = (graphStat ? Object.keys(graphStat) : []) as (keyof GraphStat)[];
    const isEmptyValues = graphStat
      ? allGraphStatKeys.filter(k => k !== 'date').every(k => graphStat[k] === 0)
      : true;
    const isEmptySensors = !Boolean(sensors?.[date]);
    const isEmptyFunnels = !Boolean(funnelValuesList[date]);
    const needForecast = date >= today && isEmptyValues && isEmptySensors && isEmptyFunnels;

    // Для каждого графика нужно создать / обновить объекты

    allChartIdsPoint.forEach(chartId => {
      // Мапим данные для этого графика за этот день

      // Если у графика есть secondary вариация, значит, может быть и предыдущее secondary значение
      const previousValue = getPreviousValue(chartId, 'secondary');

      const chartData = mapChartDataPoint({
        chartId,
        pointName,
        graphStat,
        sensors,
        previousValue,
        currentDate: date,
        needForecast,
      });

      // Предыдущее secondary значение теперь текущее
      setPreviousValue(chartId, 'secondary', chartData.secondary ?? 0);

      addChartData(chartId, chartData);
    });

    // Динамические цели воронки: включаем в общий цикл, чтобы работал прогноз

    if (analyticsGoalsMeta.length) {
      analyticsGoalsMeta.forEach(({goalId, chartId}) => {
        const series = byGoal[goalId] || [];
        const value = series[index] || 0;

        const previousSecondary = getPreviousValue(chartId, 'secondary');
        const chartData: CommonChartData = needForecast
          ? {name: pointName}
          : {
              name: pointName,
              primary: value,
              secondary: previousSecondary + value,
            };

        addChartData(chartId, chartData);

        // Обновляем предыдущие значения secondary только когда есть фактические данные
        if (!needForecast) {
          setPreviousValue(chartId, 'secondary', chartData.secondary ?? 0);
        }
      });
    }

    // Добавляем план на каждый день

    addPlan(pointName, date, mediaplansDto);

    // Добавляем скаттеры
    ALL_SCATTER_IDS.forEach(chartId => {
      switch (chartId) {
        case 'logs': {
          if (logs) {
            addScatterDataLogs('logs', pointName, logs[date]);
          }
          break;
        }
        case 'notes': {
          if (notes?.[date]) {
            addScatterDataNotes({
              chartId,
              pointName,
              date,
              buttonData: notes[date],
              refetchNotes,
            });
          }
          break;
        }
      }
    });
  });

  // Конец пробежки по датам

  // Добавляем графики для динамических целей (воронка) на основе funnelGoalStats
  // ВАЖНО: не пересобираем уже добавленные в основном цикле серии, чтобы сохранить прогноз
  if (analyticGoals && analyticGoals.length > 0 && funnelGoalStats) {
    const byGoal: Record<number, number[]> = {};
    analyticGoals.forEach(g => {
      byGoal[g.id] = new Array<number>(rechartsDates.length).fill(0);
    });

    funnelGoalStats.forEach(s => {
      const idx = dateIndexByDate.get(s.date);
      if (idx !== undefined && byGoal[s.goalId]) {
        byGoal[s.goalId][idx] += s.conversions || 0;
      }
    });

    analyticGoals.forEach(goal => {
      const chartId = goal.name as ChartIds;
      const series = byGoal[goal.id] || new Array<number>(rechartsDates.length).fill(0);
      addAnalyticGoalChartBySeries(chartId, pointNamesList, series);
    });
  }

  /*
    Записываем состояния виджетов, которые будут учитываться при их выводе в верстку.
    Правила:
    - В widgetConditions записываются только те виджеты, у которых есть параметр showConditions (из типа WidgetStat).
    - Не включаем сюда сенсоры, воронки и CR (у них нет showConditions, и только одно состояние).
   */
  // Подготовка CR-виджетов: пустые статы и надписи до выбора показателей
  const funnelCrIds = resultCharts.charts
    .map(c => c.chartId)
    .filter(id => String(id).startsWith('funnel_cr_')) as ChartIds[];

  funnelCrIds.forEach(id => {
    // До выбора показателей оставляем минимальную заглушку
    if (!resultWidgetSettings[id]) {
      resultWidgetSettings[id] = {
        stats: [
          {
            heading: 'Выбрать показатели',
          },
        ],
      };
    }
    // Обновим подпись графика: "CR, %"
    const idx = resultCharts.charts.findIndex(c => c.chartId === id);
    if (idx > -1) {
      resultCharts.charts[idx].label = 'CR, %';
    }
    // Поместим CR-виджеты в блок "Воронка"
    pushWidgetCommonId('funnel', id);
    // Минимальное состояние, чтобы виджет отрисовался
    // widgetConditions объявлен ниже, но мы присвоим позже при проходе
  });

  // Записываем состояния виджетов, которые будут учитываться при их выводе в верстку

  const widgetConditions: WidgetConditionsList = {};
  const allMediaplanUniqueIds = getAllMediaplanUniqueChartIds(mediaplansDto);

  for (let i = 0; i < resultCharts.charts.length; i++) {
    const chart = resultCharts.charts[i];
    const chartId = chart.chartId;

    if (!ALL_CHART_IDS.includes(chartId)) {
      // id не входит в список из ALL_CHART_IDS (воронка, или cr)
      continue;
    }

    const isSensor = ALL_SENSOR_CHART_IDS.includes(chartId);
    const isScatter = ALL_SCATTER_IDS.includes(chartId);

    if (isSensor || isScatter) {
      widgetConditions[chartId] = 'all';
      continue;
    }

    const isUnreachable = isPlanUnreachable({
      dateArray: rechartsDates,
      mediaplansDto,
      chartId,
    });

    const chartHasPlan = allMediaplanUniqueIds.includes(chartId);

    if (isUnreachable && chartHasPlan) {
      widgetConditions[chartId] = 'unreachable';
      continue;
    }

    if (!chartHasPlan) {
      widgetConditions[chartId] = 'not-kpi';
      continue;
    }
    if (period === 'current') {
      widgetConditions[chartId] = 'future';
      continue;
    }

    widgetConditions[chartId] = 'common';
  }

  if (analyticGoals && analyticGoals.length > 0) {
    analyticGoals.forEach(goal => {
      // Для целей аналитики по умолчанию используем общее состояние
      widgetConditions[goal.name as ChartIds] = 'common';
    });
  }

  // Разные обработки в одном цикле

  const resultWidgetSettings: WidgetSettingsList = {};
  const statsSensorsResult = statsSensors(sensors);
  const sensorColors = resultWidgetBlocks.find(item => item.id === 'sensors')?.color;

  // Мокаем статистику для динамических целей (например, «Целевой звонок»)
  if (analyticGoals && analyticGoals.length > 0) {
    const spentByDate: Record<string, number> = {};
    graphStats.forEach(g => {
      spentByDate[g.date] = (spentByDate[g.date] || 0) + (g.budget || 0);
    });

    const statsByGoal = new Map<number, {sum: number; daysWithSpend: number}>();
    (funnelGoalStats || []).forEach(s => {
      const prev = statsByGoal.get(s.goalId) || {sum: 0, daysWithSpend: 0};
      const hasSpend = (spentByDate[s.date] || 0) > 0;
      statsByGoal.set(s.goalId, {
        sum: prev.sum + (s.conversions || 0),
        daysWithSpend: prev.daysWithSpend + (hasSpend ? 1 : 0),
      });
    });

    analyticGoals.forEach(goal => {
      const rec = statsByGoal.get(goal.id) || {sum: 0, daysWithSpend: 0};
      const avg = rec.daysWithSpend > 0 ? rec.sum / rec.daysWithSpend : 0;
      const id = goal.name as unknown as ChartIds;
      resultWidgetSettings[id] = {
        calcMethod: 'day',
        stats: getStatsTypeGoal({
          avgPerDay: avg,
          sumForPeriod: rec.sum,
        }),
      };
    });
  }

  for (let i = 0; i < resultCharts.charts.length; i++) {
    const chart = resultCharts.charts[i];
    const chartId = chart.chartId;

    // Вычисляем статистику и прочие данные для виджетов

    const computed = getWidgetSettings({
      chart,
      todayPointName,
      graphComplexValuesList: graphComplexValues,
      statsSensorsResult,
      sensorColors,
      companyId,
      startDate,
      endDate,
      t,
    });

    const preset = resultWidgetSettings[chartId];
    const calcMethod = preset?.calcMethod || computed.calcMethod;
    resultWidgetSettings[chartId] = preset?.stats
      ? {
          ...computed,
          ...(calcMethod ? {calcMethod} : {}),
          stats: preset.stats,
        }
      : computed;

    /*
    Составляем список блоков для дропдаун меню с настройками виджета
    Правила:
    - hide не добавляем
    - Категории с постоянной структурой (бюджет, сенсоры, логи) игнорируем
    - Остальное распределяем по двум категориям: properties, kpi
     */

    if (chart.hide) {
      continue;
    }

    // Динамические цели должны относиться к блоку "Воронка"
    const isAnalyticGoal = analyticGoals.some(g => (g.name as unknown as ChartIds) === chartId);
    if (isAnalyticGoal) {
      pushWidgetCommonId('funnel', chartId);
      continue;
    }

    const isNeedSorting = ALL_PROPERTY_IDS.includes(chartId);

    if (isNeedSorting) {
      const showCondition = widgetConditions[chartId] || 'common';
      if (showCondition === 'not-kpi') {
        pushWidgetCommonId('properties', chartId);
      } else {
        pushWidgetCommonId('kpi', chartId);
      }
    }
  }

  /*
    Дополняем графики новыми данными:
    1. Цветовые схемы из виджетов.
    2. Информация о типе графика.
   */

  updateCharts();

  // Добавляем прогнозы

  const resultChartsWithForecast = addForecast(resultCharts, rechartsDates);

  // Сортируем графики по месту в дашборде

  const sortedResultCharts = sortCharts(resultChartsWithForecast);

  // Добавляем тултипы

  const resultChartsWithTooltips = addTooltips({
    multiChartData: sortedResultCharts,
    discreteness: activeDiscreteness,
    sensors,
    startDate,
    yesterday,
    dateList,
  });

  return {
    charts: resultChartsWithTooltips,
    blocks: resultWidgetBlocks,
    widgetSettings: resultWidgetSettings,
    widgetConditions,
    todayPointName,
  };
};

export default mapChartsData;
